import useIsTeamSideMenuRendered from '@apps/www/src/www/hooks/useIsTeamSideMenuRendered';
import SVNewBoardModal from '@apps/www/src/www/modals/SVNewBoardModal';
import SVModal from '@pkgs/shared-client/components/SVModal';
import SVSidebarContent, {
	SVSidebarContentTYPES,
} from '@pkgs/shared-client/components/SVSidebarContent';
import useEventCallback from '@pkgs/shared-client/hooks/useEventCallback';
import BoardOwnershipType from '@pkgs/shared/enums/BoardOwnershipType';
import SidebarOpenState from '@pkgs/shared/enums/SidebarOpenState';
import TeamUserRole from '@pkgs/shared/enums/TeamUserRole';
import teamUserRoleHasTeamUserRolePrivileges from '@pkgs/shared/helpers/teamUserRoleHasTeamUserRolePrivileges';
import React, { useMemo } from 'react';
import useAuthBoards from '../hooks/useAuthBoards';
import useAuthTeam from '../hooks/useAuthTeam';

const SVTeamSideMenuContainer = ({ children }: { children: React.ReactNode }) => {
	const isSideMenuRendered = useIsTeamSideMenuRendered();
	const team = useAuthTeam();
	const authBoards = useAuthBoards();
	const boards = useMemo(
		() => authBoards?.filter((board) => board.ownershipType === BoardOwnershipType.TEAM) || [],
		[authBoards],
	);

	const handleNewBoardClick = useEventCallback(() => {
		SVModal.open(SVNewBoardModal, {
			ownershipType: BoardOwnershipType.TEAM,
		});
	});

	// TODO: This is a hack to get the last thumbnails for the side menu.
	// We should find a better way to do this. With a query that gets the last 4 thumbnails for the team.
	const lastThumbnails = useMemo(() => {
		if (!team || !isSideMenuRendered) {
			return [];
		}

		const firstPassThumbnails = boards
			.filter((board) => board.thumbnails?.length > 0)
			.map((board) => board.thumbnails[0].image.thumbnail)
			.slice(0, 4);

		if (firstPassThumbnails.length >= 4) {
			return firstPassThumbnails.slice(0, 4);
		}

		const additionalThumbnails = boards
			.filter((board) => board.thumbnails?.length > 1)
			.flatMap((board) => board.thumbnails.slice(1).map((t) => t.image.thumbnail))
			.slice(0, 4 - firstPassThumbnails.length);

		return [...firstPassThumbnails, ...additionalThumbnails];
	}, [boards, team, isSideMenuRendered]);

	// If team doesn't exist or side menu shouldn't be rendered
	if (!team || !isSideMenuRendered) {
		return <>{children}</>;
	}

	const hasAdminPrivileges = teamUserRoleHasTeamUserRolePrivileges(team.role, TeamUserRole.ADMIN);

	return (
		<SVSidebarContent
			type={SVSidebarContentTYPES.TEAM}
			openState={SidebarOpenState.OPEN}
			boards={boards}
			isOwner={hasAdminPrivileges}
			onNewBoardClick={handleNewBoardClick}
			lastThumbnails={!!team.feedBoardID ? [] : lastThumbnails}
			team={team}
		>
			{children}
		</SVSidebarContent>
	);
};

export default SVTeamSideMenuContainer;
